<template>
    <div>
        <input
            type="file"
            id="files-input"
            class="hidden"
            name="images"
            :multiple="false"
            accept="image/*"
            @change="selectFiles"
            ref="profile-items-uploader-input"
        />

        <button
            :class="{'w-full rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 ring-1 shadow-xs ring-gray-300 ring-inset hover:bg-gray-50 cursor-pointer': true, 'active-uploading': files.length}"
            @click.prevent="clickSelectFiles"
            :disabled="files.length"
        >
            <span v-if="files.length">{{ $t('profile_uploader.uploading') }}</span>
            <span v-if="!files.length">{{ $t('profile_uploader.upload_button') }}</span>
        </button>
    </div>

    <UploadLimitModalAlertComponent
        :open="showUploadLimitModal"
        @close="showUploadLimitModal = false"
        type="profile_limit"
    />
</template>

<script lang="ts">
import {defineComponent} from "vue";
import {mapActions, mapGetters} from "vuex";
import {ResponseError} from "@/types";
import UploadLimitModalAlertComponent from "@/components/Galleries/Modals/UploadLimitModalAlertComponent.vue";

export default defineComponent({
    name: "FileUploaderComponent",
    components: {UploadLimitModalAlertComponent},

    props: {
        folder: Object,
        group: String,
    },

    data() {
        return {
            errors: [] as string[],

            files: [] as File[],

            isUploadingFiles: false,

            uploadedFiles: [] as File[],

            isDragging: false,

            showUploadLimitModal: false,
        }
    },

    computed: {
        ...mapGetters({
            items: "profile/items",
            user: "user/user"
        }),

        totalFiles(): Number {
            return this.files.length;
        },

        totalSize(): Number {
            let totalSize = 0;

            for (let file of this.files) {
                totalSize += file.size;
            }

            return totalSize;
        },

        uploadedSize(): Number {
            let totalSize = 0;

            for (let file of this.uploadedFiles) {
                totalSize += file.size;
            }

            return totalSize;
        },

        uploadingProgress(): Number {
            return (this.uploadedFiles.length / (this.totalFiles as number)) * 100;
        }
    },

    mounted() {
        document.body.addEventListener('dragover', this.handleDragOver);
        document.body.addEventListener('dragleave', this.handleDragLeave);
        document.body.addEventListener('drop', this.handleDrop);
        window.addEventListener('beforeunload', this.handleBeforeUnload);
    },

    beforeUnmount() {
        document.body.removeEventListener('dragover', this.handleDragOver);
        document.body.removeEventListener('dragleave', this.handleDragLeave);
        document.body.removeEventListener('drop', this.handleDrop);
        window.removeEventListener('beforeunload', this.handleBeforeUnload);
    },

    methods: {
        ...mapActions({
            uploadProfileFile: "profile/uploadFile",

            errorAlert: "alerts/error",
        }),

        async selectFiles(event: Event) {
            const input = event.target as HTMLInputElement;
            this.files = [...(input.files as FileList)];

            this.uploadedFiles = [];
            this.errors = [];

            input.disabled = true;

            let filesArray = Array.from(this.files);
            let chunks = [];

            for(let i = 0; i < filesArray.length; i += 2) {
                chunks.push(filesArray.slice(i, i + 2));
            }

            for (let chunk of chunks) {
                const promises = chunk.map(async (file) => {
                    try {
                        await this.uploadFile(file);

                        this.uploadedFiles.push(file);
                    } catch (error: any) {
                        this.errorAlert({
                            message: error.message,
                            timeout: 30
                        });

                        //this.errors.push(`Помилка завнтаження файлу ${file.name}: <br>${error.message}`);
                    }
                });

                await Promise.all(promises);
            }

            input.value = "";
            input.disabled = false;

            setTimeout(() => {
                this.files = [];

                setTimeout(() => {
                    this.uploadedFiles = [];
                }, 1000);
            }, 1000);
        },

        async uploadFile(file: File) {
            return await this.uploadProfileFile({
                file,
                group: this.group,
            }).then(() => {
                //
            })
                .catch(({type, data}) => {
                    if(type == ResponseError.TooLarge) {
                        throw new Error(this.$t('profile_uploader.error_too_large'));
                    }

                    if (data.message) {
                        throw new Error(data.message);
                    }

                    throw new Error(this.$t('profile_uploader.error_unknown'));
                });
        },

        clickSelectFiles() {
            if(this.user.subscription_type == 'free') {
                this.showUploadLimitModal = true;
                return;
            }

            const input = this.$refs["profile-items-uploader-input"] as HTMLInputElement;

            input.click();
        },

        bytesToSize(bytes: number) {
            const sizes = ['Байт', 'КБ', 'МБ', 'ГБ', 'ТБ'];

            if (bytes == 0) return '0 Байт';

            const i = (Math.floor(Math.log(bytes) / Math.log(1024)));

            return Math.round(bytes / Math.pow(1024, i)) + ' ' + sizes[i];
        },

        handleDragOver(event: DragEvent) {
            event.preventDefault();
            event.stopPropagation();

            this.isDragging = true;
        },

        handleDragLeave(event: DragEvent) {
            event.preventDefault();
            event.stopPropagation();

            this.isDragging = false;
        },

        handleDrop(event: DragEvent) {
            event.preventDefault();
            event.stopPropagation();

            this.isDragging = false;

            const files = Array.from(event.dataTransfer?.files || []);

            if (files.length) {
                const customEvent = {
                    target: {
                        files: files as unknown as FileList
                    }
                };

                this.selectFiles(customEvent as unknown as Event);
            }
        },

        handleBeforeUnload(event: BeforeUnloadEvent) {
            if (this.files.length) {
                const message = this.$t('profile_uploader.unload_warning');

                event.returnValue = message;

                return message;
            }
        }
    },
});
</script>

<style lang="scss" scoped>
.is-uploading-btn {
    svg {
        display: none;
    }

    &.active-uploading {
        svg {
            display: block;
        }
    }
}
</style>